import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { Fragment, useCallback, useState } from 'react';
import { recentTourneyWinnersParser } from '../../api/inbound/commonInbounds/recentTourneyWinnersInbound';
import { PanelItemDivider } from '../../panels/panelItemDivider';
import { ENTRY_SIDE_PANEL_DIVIDER_COLOR } from '../../styles/colors';
import { getListRevealMotionProps } from '../../utils/motionUtils';
import { isLocalhostEnvironment, range } from '../../utils/utils';
import { EntrySidePanel } from './components/entrySidePanel';
// API endpoint will probably throw a CORS error locally, so use some mock data instead of fetching anything
const USE_MOCK_DATA = isLocalhostEnvironment();
// the server may return more than this, but we don't want to show too many on the entry scene
const MAX_NUM_TO_SHOW = 5;
const LIST_CONTAINER_CSS = css `
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const ROW_CSS = css `
  display: flex;
  flex-flow: row wrap;
  gap: 0 5px;
`;
const MTT_NAME_CSS = css `
  color: #999;
  flex-grow: 1;
  text-align: end;
`;
function createMockRecentTourneyWinners() {
    // generate enough to need the 'More' button:
    const totalNumMockTourneyWinners = MAX_NUM_TO_SHOW * 2;
    return range(totalNumMockTourneyWinners).map((i) => ({
        winnerId: i + 1,
        winnerName: `player${i + 1}`,
        mttName: i % 2 === 0 ? `Some Really Cool Tourney ${i + 1}` : `Another Fun Game ${i + 1}`,
        autoMttId: i + 1
    }));
}
async function fetchRecentTourneyWinners() {
    if (USE_MOCK_DATA) {
        return createMockRecentTourneyWinners();
    }
    try {
        const response = await fetch('/api_recent_tourney_winners.php');
        if (!response.ok) {
            return;
        }
        const jsonStr = await response.text();
        const newRecentTourneyWinners = recentTourneyWinnersParser(jsonStr);
        if (newRecentTourneyWinners.length) {
            return newRecentTourneyWinners;
        }
    }
    catch {
        // do nothing
    }
}
export const EntryRecentTourneyWinnersContainer = () => {
    const [hasClickedMore, setHasClickedMore] = useState(false);
    const { data: recentTourneyWinners } = useQuery({
        queryKey: ['recent-tourney-winners'],
        queryFn: fetchRecentTourneyWinners
    });
    const onClickMore = useCallback(() => {
        setHasClickedMore(true);
    }, []);
    if (!recentTourneyWinners) {
        return null;
    }
    const recentTourneyWinnersToShow = hasClickedMore || recentTourneyWinners.length <= MAX_NUM_TO_SHOW
        ? recentTourneyWinners
        : recentTourneyWinners.slice(0, MAX_NUM_TO_SHOW);
    return (_jsxs(EntrySidePanel, { title: '\uD83C\uDFC6 Recent Tourney Winners', children: [_jsx("div", { css: LIST_CONTAINER_CSS, children: recentTourneyWinnersToShow.map(({ winnerName, mttName }, i) => {
                    return (_jsxs(Fragment, { children: [i > 0 && _jsx(PanelItemDivider, { color: ENTRY_SIDE_PANEL_DIVIDER_COLOR, thicknessPx: 2, marginPx: 0 }), _jsxs(motion.div, { css: ROW_CSS, ...getListRevealMotionProps(i, { startX: 40 }), children: [_jsx("div", { children: _jsx(Typography, { variant: 'subtitle2', children: winnerName }) }), _jsx("div", { css: MTT_NAME_CSS, children: _jsx(Typography, { variant: 'subtitle2', children: mttName }) })] })] }, i));
                }) }), recentTourneyWinnersToShow.length < recentTourneyWinners.length && (_jsx("div", { children: _jsx(Button, { variant: 'outlined', onClick: onClickMore, size: 'small', children: "More" }) }))] }));
};
